import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { nameToHex, colors, ColorName } from '@/styles';
import { isColorName } from '@/styles/colorUtils';
import { StyledComponent } from '@/helpers';

export type LoadingDotsProps = StyledComponent & (LoadingDotsWithOldName | LoadingDotsWithColor);

type LoadingDotsWithOldName = {
  color?: Extract<
    ColorName,
    'battleship' | 'taos-sky' | 'traffic-cone' | 'boulder' | 'kermit' | 'bluebee' | 'tutu'
  >;
  size?: number;
};

type LoadingDotsWithColor = {
  color?: string;
  size?: number;
};

/**
 * Loading Dots
 */

const bbColorNames: ColorName[] = [
  'battleship',
  'taosSky',
  'trafficCone',
  'boulder',
  'kermit',
  'bluebee',
  'tutu',
];

function LoadingDots({ className, color = colors.trafficCone }: LoadingDotsProps) {
  if (isColorName(color)) {
    console.warn(
      'Using soon to be deprecated version of color prop. ' +
        'You can now pass a hex value directly into color',
      color,
    );
  }

  return (
    <div className={className}>
      <div className="dots">
        <div className="bounce1" />
        <div className="bounce2" />
        <div className="bounce3" />
      </div>
    </div>
  );
}

/* eslint-disable react/no-unused-prop-types */
LoadingDots.propTypes = {
  /** Emotion base style */
  className: PropTypes.string,
  /** Color of the prop */
  color: PropTypes.string,
  /** Size of label text, effects size of dots */
  size: PropTypes.number,
};

LoadingDots.defaultProps = {
  className: '',
  color: colors.trafficCone,
  size: 1,
};

const styledLoadingDots = styled(LoadingDots)`
  .dots {
    text-align: center;

    div {
      &:first-of-type {
        animation-delay: -0.32s;
      }

      &:nth-of-type(2) {
        animation-delay: -0.16s;
      }
    }
  }

  @keyframes bouncedelay {
    0%,
    80%,
    100% {
      transform: scale(0);
    }

    40% {
      transform: scale(1);
    }
  }
  ${({ color = colors.trafficCone, size = 1 }: LoadingDotsProps) => `
    color: ${isColorName(color) && bbColorNames.includes(color) ? nameToHex(color) : color};
    font-size: ${size}rem;

    .dots div {
      width: ${size / 2}rem;
      height: ${size / 2}rem;
      background-color: ${
        isColorName(color) && bbColorNames.includes(color) ? nameToHex(color) : color
      };
      margin: 0;
      border-radius: 100%;
      display: inline-block;
      animation: bouncedelay 1.4s infinite ease-in-out;
      animation-fill-mode: both;
    }
    `}
`;

export default styledLoadingDots;
